import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import {
    FaLeaf,
    FaFlask,
    FaSeedling,
    FaWater,
    FaTimes,
    FaChevronDown,
    FaCheckCircle
} from 'react-icons/fa';

const IngredientBit = ({ ingredients, freeFrom }) => {
    const [expandedIngredient, setExpandedIngredient] = useState(null);

    const ingredientDetails = {
        "15% Stable Vitamin C": {
            description: "A potent antioxidant that brightens complexion and protects against environmental damage.",
            benefits: ["Illuminates", "Protects", "Repairs"],
            source: "Derived from natural citrus"
        },
        "Camellia Japonica Extract": {
            description: "Japanese camellia oil rich in antioxidants and omega fatty acids for intense hydration.",
            benefits: ["Hydrates", "Soothes", "Nourishes"],
            source: "Japanese camellia flowers"
        },
        "Hyaluronic Acid Complex": {
            description: "Multi-molecular weight HA that delivers hydration to multiple skin layers.",
            benefits: ["Plumps", "Hydrates", "Smooths"],
            source: "Bio-fermented"
        },
        "Niacinamide": {
            description: "Vitamin B3 that improves skin barrier, reduces redness, and minimizes pores.",
            benefits: ["Strengthens", "Calms", "Refines"],
            source: "Lab-created, skin-identical"
        },
        "Radiance Complex™": {
            description: "Our proprietary blend of botanical extracts that work synergistically to enhance luminosity.",
            benefits: ["Brightens", "Even Tone", "Revitalizes"],
            source: "Patented botanical blend"
        },
        "Botanical Antioxidants": {
            description: "A cocktail of green tea, turmeric, and grape seed extracts for maximum protection.",
            benefits: ["Protects", "Fights Aging", "Strengthens"],
            source: "Organic botanicals"
        }
    };

    return (
        <div className="space-y-12 md:space-y-16 px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center space-y-4 md:space-y-6">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-sage/10 rounded-full">
                        <FaLeaf className="w-3 h-3 md:w-3.5 md:h-3.5 text-sage" />
                        <span className="text-xs md:text-sm tracking-wider md:tracking-widest uppercase text-sage">
                            Clean Formulation
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-light leading-tight md:leading-normal">
                        Science, <span className="italic text-sage">Purity</span>, &{' '}
                        <span className="italic text-champagne">Potency</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-base sm:text-lg md:text-xl text-charcoal/60 max-w-lg md:max-w-2xl mx-auto px-2 md:px-0">
                        Every ingredient is meticulously selected for efficacy, safety, and sustainability
                    </p>
                </Reveal>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
                {/* Key Ingredients - Left Column */}
                <div className="space-y-4 md:space-y-6">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-6 md:mb-8">
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-champagne/10 flex items-center justify-center flex-shrink-0">
                                <FaFlask className="w-4 h-4 md:w-5 md:h-5 text-champagne" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-light text-charcoal">Key Actives</h3>
                        </div>
                    </Reveal>

                    <StaggerContainer stagger={0.05}>
                        <div className="space-y-3 md:space-y-4">
                            {ingredients.map((ingredient, index) => (
                                <motion.div
                                    key={index}
                                    variants={{
                                        hidden: { opacity: 0, x: -20 },
                                        visible: { opacity: 1, x: 0 }
                                    }}
                                    className="w-full"
                                >
                                    <button
                                        onClick={() => setExpandedIngredient(
                                            expandedIngredient === ingredient ? null : ingredient
                                        )}
                                        className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-champagne/30 focus:rounded-2xl"
                                    >
                                        <div className="flex items-center justify-between p-4 md:p-6 rounded-xl md:rounded-2xl bg-white shadow-subtle hover:shadow-lift transition-all duration-300">
                                            <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center flex-shrink-0 group-hover:from-champagne/20 group-hover:to-sage/20 transition-all duration-300">
                                                    {ingredient.includes('Vitamin') ? (
                                                        <FaFlask className="w-4 h-4 md:w-5 md:h-5 text-champagne" />
                                                    ) : ingredient.includes('Botanical') ? (
                                                        <FaLeaf className="w-4 h-4 md:w-5 md:h-5 text-sage" />
                                                    ) : ingredient.includes('Extract') ? (
                                                        <FaSeedling className="w-4 h-4 md:w-5 md:h-5 text-sage" />
                                                    ) : (
                                                        <FaWater className="w-4 h-4 md:w-5 md:h-5 text-champagne" />
                                                    )}
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <h4 className="font-medium text-charcoal group-hover:text-champagne transition-colors truncate md:truncate-none text-sm md:text-base">
                                                        {ingredient}
                                                    </h4>
                                                    <div className="flex items-center gap-1 md:gap-2 mt-1 flex-wrap">
                                                        {ingredientDetails[ingredient]?.benefits.map((benefit, i) => (
                                                            <span
                                                                key={i}
                                                                className="text-xs px-1.5 md:px-2 py-0.5 md:py-1 bg-nude/30 rounded-full text-charcoal/60 whitespace-nowrap"
                                                            >
                                                                {benefit}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`ml-2 md:ml-4 transition-transform duration-300 flex-shrink-0 ${
                                                expandedIngredient === ingredient ? 'rotate-180' : ''
                                            }`}>
                                                <FaChevronDown className="w-3 h-3 md:w-4 md:h-4 text-charcoal/40" />
                                            </div>
                                        </div>

                                        <AnimatePresence>
                                            {expandedIngredient === ingredient && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="p-4 md:p-6 bg-white/50 rounded-b-xl md:rounded-b-2xl border-t border-nude/50">
                                                        <p className="text-charcoal/70 text-sm md:text-base mb-3 md:mb-4">
                                                            {ingredientDetails[ingredient]?.description}
                                                        </p>
                                                        <div className="flex items-center gap-2 text-xs md:text-sm text-charcoal/50">
                                                            <FaSeedling className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                                            <span>Source: {ingredientDetails[ingredient]?.source}</span>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                    </StaggerContainer>
                </div>

                {/* Free From - Right Column */}
                <div className="space-y-4 md:space-y-6">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-6 md:mb-8">
                            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-sage/10 flex items-center justify-center flex-shrink-0">
                                <FaCheckCircle className="w-4 h-4 md:w-5 md:h-5 text-sage" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-light text-charcoal">Free From</h3>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div className="bg-gradient-to-br from-white to-pearl rounded-xl md:rounded-2xl p-6 md:p-8 shadow-subtle">
                            <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
                                {freeFrom.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className="flex items-center gap-2 md:gap-3 p-3 md:p-4 rounded-lg md:rounded-xl bg-white shadow-soft"
                                    >
                                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-sage/10 flex items-center justify-center flex-shrink-0">
                                            <FaTimes className="w-3 h-3 md:w-4 md:h-4 text-sage" />
                                        </div>
                                        <span className="font-medium text-charcoal text-sm md:text-base truncate">
                                            {item}
                                        </span>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-nude/50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div>
                                        <h4 className="font-medium text-charcoal mb-1 md:mb-2 text-sm md:text-base">
                                            Dermatologist Inspired
                                        </h4>
                                        <p className="text-xs md:text-sm text-charcoal/60 leading-relaxed">
                                            Formulated under dermatological supervision for optimal efficacy and safety
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-charcoal mb-1 md:mb-2 text-sm md:text-base">
                                            Clinically Tested
                                        </h4>
                                        <p className="text-xs md:text-sm text-charcoal/60 leading-relaxed">
                                            94% of users reported visible improvement in skin radiance after 4 weeks
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>

            {/* Philosophy Note */}
            <Reveal delay={0.6}>
                <div className="text-center pt-8 md:pt-12">
                    <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-champagne/10 to-sage/10 rounded-full max-w-lg mx-auto">
                        <span className="text-xs md:text-sm text-charcoal/60 text-center">
                            Each ingredient serves a purpose. Nothing is added without intention.
                        </span>
                    </div>
                </div>
            </Reveal>
        </div>
    );
};

export default IngredientBit;